import {
  DownOutlined,
  DownloadOutlined,
  MailOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import { Button, Dropdown } from 'antd';
import { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ActionPopoverElement } from '../../components/ActionPopoverButton';
import SelectionActionBar from '../../components/SelectionActionBar';
import colors from '../../theme/colors';
import { CertificateActionAttendee } from '../hooks/useAttendeeCertificateActions';
import { StatusAction, statusActions } from '../utils/attendeeStatusActions';
import OpenClassAttendeeStatusBox from './OpenClassAttendeeStatusBox';

export const StatusActionLabel: React.FC<{ action: StatusAction }> = ({
  action,
}) => {
  const { t } = useTranslation();

  return action.key === 'clear' ? (
    <div className="flex gap-2">
      <MinusCircleOutlined style={{ color: colors.safeLifeDarker }} />
      {t('components.OpenClassAttendeeActions.clearResult')}
    </div>
  ) : (
    <OpenClassAttendeeStatusBox status={action.status} />
  );
};

export type RowActionsParams<T extends CertificateActionAttendee> = {
  attendee: T;
  canIssueCertificate: (attendee: T) => boolean;
  // Which action the attendee already sits at, so it can be greyed out
  isAtStatus: (attendee: T, action: StatusAction) => boolean;
  // Why the pass is off the table for this row, nothing when it isn't
  passBlocked?: (attendee: T) => string | undefined;
  t: TFunction;
  onApplyStatus: (action: StatusAction, attendees: T[]) => void;
  onDownload: (attendees: T[]) => void;
  onEmail: (attendee: T) => void;
};

export const attendeeRowActionElements = <T extends CertificateActionAttendee>({
  attendee,
  canIssueCertificate,
  isAtStatus,
  passBlocked,
  t,
  onApplyStatus,
  onDownload,
  onEmail,
}: RowActionsParams<T>): ActionPopoverElement[] => {
  const earned = canIssueCertificate(attendee);
  const noCertificate = earned
    ? undefined
    : t('components.OpenClassAttendeeActions.noCertificateEarned');

  return [
    ...statusActions.map((action) => {
      const blocked = attendee.certificateIssued
        ? t('components.OpenClassAttendeeActions.certificateIssuedBlocked')
        : action.key === 'passed'
          ? passBlocked?.(attendee)
          : undefined;

      return {
        rowElement: <StatusActionLabel action={action} />,
        action: () => onApplyStatus(action, [attendee]),
        disabled: isAtStatus(attendee, action) || Boolean(blocked),
        tooltip: blocked,
      };
    }),
    {
      rowElement: (
        <span>
          {t('components.OpenClassAttendeeActions.downloadCertificate')}
        </span>
      ),
      action: () => onDownload([attendee]),
      disabled: !earned,
      tooltip: noCertificate,
    },
    {
      rowElement: (
        <span>{t('components.OpenClassAttendeeActions.emailCertificate')}</span>
      ),
      action: () => onEmail(attendee),
      disabled: !earned,
      tooltip: noCertificate,
    },
  ];
};

type AttendeeSelectionBarProps = {
  certificateCount: number;
  saving: boolean;
  selectedCount: number;
  onChangeStatus: (action: StatusAction) => void;
  onDownload: () => void;
  onEmail: () => void;
};

const barButton =
  'flex border-1 shadow-none rounded h-10 items-center font-medium';

export const AttendeeSelectionBar: React.FC<AttendeeSelectionBarProps> = ({
  certificateCount,
  saving,
  selectedCount,
  onChangeStatus,
  onDownload,
  onEmail,
}) => {
  const { t } = useTranslation();

  return (
    <SelectionActionBar
      count={selectedCount}
      label={t('components.OpenClassAttendeeActions.attendeesSelected', {
        count: selectedCount,
      })}>
      <Dropdown
        disabled={saving}
        menu={{
          items: statusActions.map((action) => ({
            key: action.key,
            label: <StatusActionLabel action={action} />,
          })),
          onClick: ({ key }) => {
            const action = statusActions.find(
              (candidate) => candidate.key === key,
            );
            if (action) onChangeStatus(action);
          },
        }}
        placement="bottomRight"
        trigger={['click']}>
        <Button className={barButton} disabled={saving} loading={saving}>
          {t('components.OpenClassAttendeeActions.changeStatus')}
          <DownOutlined className="h-3.5 w-3.5" />
        </Button>
      </Dropdown>
      {certificateCount > 0 && (
        <>
          <Button
            className={barButton}
            disabled={saving}
            icon={<DownloadOutlined />}
            loading={saving}
            onClick={onDownload}>
            {t('components.OpenClassAttendeeActions.downloadCertificates', {
              count: certificateCount,
            })}
          </Button>
          <Button
            className={barButton}
            disabled={saving}
            icon={<MailOutlined />}
            loading={saving}
            onClick={onEmail}>
            {t('components.OpenClassAttendeeActions.emailCertificates', {
              count: certificateCount,
            })}
          </Button>
        </>
      )}
    </SelectionActionBar>
  );
};
